import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";
import {
  FAQS,
  LOAN_AMOUNT_OPTIONS,
  PROGRAM,
  PROPERTY_TYPE_OPTIONS,
  TIMELINE_OPTIONS,
} from "@/mock";
import {
  clearAllLeads,
  createLead,
  getLeadCount,
} from "@/lib/leadsApi";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Clock,
  FileClock,
  Landmark,
  Phone,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { z } from "zod";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_project-finance-20/artifacts/8kxms4cx_ChatGPT%20Image%20Dec%2030%2C%202025%2C%2006_32_37%20AM.png";

const schema = z.object({
  name: z.string().min(2, "Please enter your name"),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().min(7, "Please enter a phone number"),
  company: z.string().min(2, "Please enter your company"),
  propertyType: z.string().min(2, "Select a property type"),
  loanAmount: z.string().min(1, "Select a loan amount"),
  timeline: z.string().min(1, "Select a timeline"),
  message: z.string().min(10, "Tell us a bit about the project"),
});

const NavLink = ({ href, children }) => (
  <a
    href={href}
    className="text-sm font-medium text-[hsl(var(--cs-ink-2))] hover:text-[hsl(var(--cs-ink))] transition-colors"
  >
    {children}
  </a>
);

const StatPill = ({ icon: Icon, label, value }) => (
  <div className="relative overflow-hidden rounded-[1.05rem] border border-black/10 bg-white/75 backdrop-blur-xl px-4 py-3">
    <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-[radial-gradient(circle_at_center,rgba(232,196,144,0.18),transparent_65%)]" />
    <div className="relative flex items-start gap-3">
      <div className="mt-0.5 rounded-xl border border-black/10 bg-white/80 p-2">
        <Icon className="h-4 w-4 text-[hsl(var(--cs-green))]" />
      </div>
      <div>
        <div className="text-xs text-[hsl(var(--cs-ink-2))]">{label}</div>
        <div className="text-sm font-semibold text-[hsl(var(--cs-ink))]">{value}</div>
      </div>
    </div>
  </div>
);

const SectionTitle = ({ eyebrow, title, desc, align = "left" }) => (
  <div className={cn("max-w-3xl", align === "center" && "mx-auto text-center")}>
    {eyebrow ? (
      <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/75 px-3 py-1 text-xs font-medium text-[hsl(var(--cs-ink-2))] backdrop-blur-xl">
        <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--cs-green))]" />
        <span>{eyebrow}</span>
      </div>
    ) : null}
    <h2 className="mt-4 text-2xl md:text-4xl font-semibold tracking-tight text-[hsl(var(--cs-ink))]">
      {title}
    </h2>
    {desc ? (
      <p className="mt-3 text-sm md:text-base text-[hsl(var(--cs-ink-2))] leading-relaxed">{desc}</p>
    ) : null}
  </div>
);

export default function LoansLanding() {
  const [activeSection, setActiveSection] = useState("overview");
  const [submitting, setSubmitting] = useState(false);
  const [values, setValues] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    propertyType: "",
    loanAmount: "",
    timeline: "",
    message: "",
  });
  const [touched, setTouched] = useState({});
  const [recentCount, setRecentCount] = useState(0);

  const overviewRef = useRef(null);
  const termsRef = useRef(null);
  const processRef = useRef(null);
  const faqRef = useRef(null);
  const contactRef = useRef(null);

  const errors = useMemo(() => {
    const result = schema.safeParse(values);
    if (result.success) return {};
    const map = {};
    for (const issue of result.error.issues) map[issue.path[0]] = issue.message;
    return map;
  }, [values]);

  const hasError = (name) => Boolean(touched[name] && errors[name]);

  const scrollTo = (ref, sectionKey) => {
    setActiveSection(sectionKey);
    ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    // Lightweight section observer for nav cue
    const nodes = [
      { key: "overview", node: overviewRef.current },
      { key: "terms", node: termsRef.current },
      { key: "process", node: processRef.current },
      { key: "faq", node: faqRef.current },
      { key: "contact", node: contactRef.current },
    ].filter((x) => x.node);

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0))[0];
        if (!visible) return;
        const match = nodes.find((n) => n.node === visible.target);
        if (match) setActiveSection(match.key);
      },
      { root: null, rootMargin: "-30% 0px -60% 0px", threshold: [0.1, 0.25, 0.5] }
    );

    nodes.forEach((n) => obs.observe(n.node));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const c = await getLeadCount();
        if (mounted) setRecentCount(c);
      } catch {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      name: true,
      email: true,
      phone: true,
      company: true,
      propertyType: true,
      loanAmount: true,
      timeline: true,
      message: true,
    });

    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    setSubmitting(true);
    try {
      const created = await createLead({
        name: parsed.data.name,
        email: parsed.data.email,
        phone: parsed.data.phone,
        company: parsed.data.company,
        property_type: parsed.data.propertyType,
        loan_amount: parsed.data.loanAmount,
        timeline: parsed.data.timeline,
        message: parsed.data.message,
        source: "loans_landing",
      });
      const c = await getLeadCount();
      setRecentCount(c);
      toast.success("Thanks — we received your request.", {
        description: "A team member will reach out shortly.",
        action: {
          label: "Open CrowdStructure",
          onClick: () => window.open("https://www.crowdstructure.com", "_blank"),
        },
      });
      setValues({
        name: "",
        email: "",
        phone: "",
        company: "",
        propertyType: "",
        loanAmount: "",
        timeline: "",
        message: "",
      });
      setTouched({});

      // Subtle local confirmation
      if (created?.id) {
        // no-op; reserved for future analytics
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const clearDemo = async () => {
    // NOTE: This is an admin convenience for the demo environment.
    // If you prefer, we can remove this button or protect it behind an admin key.
    try {
      await clearAllLeads();
      const c = await getLeadCount();
      setRecentCount(c);
      toast.message("Saved requests cleared.", {
        description: "All saved requests were removed from the database.",
      });
    } catch {
      toast.error("Could not clear requests. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--cs-bg))] text-[hsl(var(--cs-ink))]">
      {/* Ambient layers */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(1100px_700px_at_10%_0%,rgba(11,58,117,0.10),transparent_60%),radial-gradient(900px_520px_at_90%_10%,rgba(32,167,140,0.10),transparent_62%),radial-gradient(700px_500px_at_50%_95%,rgba(32,167,140,0.10),transparent_60%)]" />
        <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay bg-[url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22 viewBox=%220 0 120 120%22%3E%3Cfilter id=%22n%22 x=%220%22 y=%220%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.8%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22120%22 height=%22120%22 filter=%22url(%23n)%22 opacity=%220.35%22/%3E%3C/svg%3E')]" />
      </div>

      {/* Trust bar */}
      <div className="border-b border-black/10 bg-white/60 backdrop-blur-xl">
        <div className="mx-auto max-w-6xl px-4 py-2">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-[hsl(var(--cs-ink-2))]">
            <div className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[hsl(var(--cs-green))]" />
              <span><span className="font-semibold text-[hsl(var(--cs-ink))]">Rates from {PROGRAM.ratesStartingAt}</span> (subject to underwriting)</span>
            </div>
            <div className="hidden sm:block h-4 w-px bg-black/10" />
            <div className="inline-flex items-center gap-2">
              <Clock className="h-4 w-4 text-[hsl(var(--cs-green))]" />
              <span><span className="font-semibold text-[hsl(var(--cs-ink))]">Close in {PROGRAM.closingTimeline}</span> with complete package</span>
            </div>
            <div className="hidden sm:block h-4 w-px bg-black/10" />
            <div className="inline-flex items-center gap-2">
              <FileClock className="h-4 w-4 text-[hsl(var(--cs-green))]" />
              <span><span className="font-semibold text-[hsl(var(--cs-ink))]">Snap draws</span> funded in ~24 hours</span>
            </div>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-40 border-b border-black/10 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto max-w-6xl px-4">
          <div className="flex h-16 items-center justify-between gap-4">
            <a
              href="#overview"
              onClick={(e) => {
                e.preventDefault();
                scrollTo(overviewRef, "overview");
              }}
              className="flex items-center gap-3"
            >
              <img
                src={LOGO_URL}
                alt="CrowdStructure"
                className="h-9 w-auto rounded-md bg-white p-1"
              />
              <div className="hidden sm:block leading-tight">
                <div className="text-sm font-semibold tracking-tight">CrowdStructure</div>
                <div className="text-xs text-[hsl(var(--cs-ink-2))]">New Construction Lending</div>
              </div>
            </a>

            <nav className="hidden md:flex items-center gap-6">
              <button
                className={cn(
                  "text-sm font-medium transition-colors",
                  activeSection === "terms" ? "text-[hsl(var(--cs-ink))]" : "text-[hsl(var(--cs-ink-2))] hover:text-[hsl(var(--cs-ink))]"
                )}
                onClick={() => scrollTo(termsRef, "terms")}
              >
                Terms
              </button>
              <button
                className={cn(
                  "text-sm font-medium transition-colors",
                  activeSection === "process" ? "text-[hsl(var(--cs-ink))]" : "text-[hsl(var(--cs-ink-2))] hover:text-[hsl(var(--cs-ink))]"
                )}
                onClick={() => scrollTo(processRef, "process")}
              >
                Draws & Process
              </button>
              <button
                className={cn(
                  "text-sm font-medium transition-colors",
                  activeSection === "faq" ? "text-[hsl(var(--cs-ink))]" : "text-[hsl(var(--cs-ink-2))] hover:text-[hsl(var(--cs-ink))]"
                )}
                onClick={() => scrollTo(faqRef, "faq")}
              >
                FAQ
              </button>
              <button
                className={cn(
                  "text-sm font-medium transition-colors",
                  activeSection === "contact" ? "text-[hsl(var(--cs-ink))]" : "text-[hsl(var(--cs-ink-2))] hover:text-[hsl(var(--cs-ink))]"
                )}
                onClick={() => scrollTo(contactRef, "contact")}
              >
                Contact
              </button>
            </nav>

            <div className="flex items-center gap-3">
              <a
                href="https://www.crowdstructure.com"
                target="_blank"
                rel="noreferrer"
                className="hidden sm:inline-flex"
              >
                <Button
                  variant="secondary"
                  className="border-black/10 bg-white/75 text-[hsl(var(--cs-ink))] hover:bg-white/90"
                >
                  Visit website
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <Button
                className="bg-[hsl(var(--cs-navy))] bg-[linear-gradient(180deg,hsl(var(--cs-navy)),hsl(var(--cs-navy)/0.92))] hover:shadow-[0_12px_28px_rgba(15,102,84,0.14)] transition-colors text-white hover:bg-[hsl(var(--cs-navy)/0.94)] shadow-[0_10px_22px_rgba(15,102,84,0.12)]"
                onClick={() => scrollTo(contactRef, "contact")}
              >
                Request terms
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section ref={overviewRef} id="overview" className="relative">
        <div className="mx-auto max-w-6xl px-4 pt-14 pb-10 md:pt-20 md:pb-14">
          <div className="grid gap-10 md:grid-cols-12 md:items-start">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/75 px-3 py-1 text-xs text-[hsl(var(--cs-ink-2))] backdrop-blur-xl">
                <ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--cs-green))]" />
                <span>{PROGRAM.eligibility}</span>
              </div>

              <h1 className="mt-5 text-3xl md:text-5xl font-semibold tracking-tight">
                New construction capital for projects that need speed — not bureaucracy.
              </h1>
              <p className="mt-4 text-base md:text-lg text-[hsl(var(--cs-ink-2))] leading-relaxed max-w-xl">
                Fund land and construction with a program built for experienced developers.
                Clear terms, fast draws, and a closing process designed to stay on schedule.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Button
                  size="lg"
                  className="bg-[hsl(var(--cs-navy))] bg-[linear-gradient(180deg,hsl(var(--cs-navy)),hsl(var(--cs-navy)/0.92))] hover:shadow-[0_12px_28px_rgba(15,102,84,0.14)] transition-colors text-white hover:bg-[hsl(var(--cs-navy)/0.94)]"
                  onClick={() => scrollTo(contactRef, "contact")}
                >
                  Get a quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="secondary"
                  className="border-black/10 bg-white/75 text-[hsl(var(--cs-ink))] hover:bg-white/10"
                  onClick={() => scrollTo(termsRef, "terms")}
                >
                  See program snapshot
                </Button>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                <Badge className="bg-white/75 text-[hsl(var(--cs-ink))] border border-black/10">Rates from {PROGRAM.ratesStartingAt}</Badge>
                <Badge className="bg-white/75 text-[hsl(var(--cs-ink))] border border-black/10">Up to 90% LTC</Badge>
                <Badge className="bg-white/75 text-[hsl(var(--cs-ink))] border border-black/10">$200K–$30M</Badge>
                <Badge className="bg-white/75 text-[hsl(var(--cs-ink))] border border-black/10">Close in {PROGRAM.closingTimeline}</Badge>
              </div>

              <p className="mt-5 text-xs text-[hsl(var(--cs-ink-2))] max-w-2xl">
                Disclaimer: Rates and terms are subject to underwriting and may change.
                This page is for informational purposes only and is not a commitment to lend.
              </p>
            </div>

            <div className="md:col-span-5">
              <div className="relative rounded-[1.25rem] border border-black/10 bg-white/75 backdrop-blur-xl p-5 md:p-6 overflow-hidden">
                <div className="absolute -left-24 -top-24 h-64 w-64 rounded-full bg-[radial-gradient(circle_at_center,rgba(24,145,255,0.18),transparent_60%)]" />
                <div className="absolute -right-24 -bottom-24 h-64 w-64 rounded-full bg-[radial-gradient(circle_at_center,rgba(232,196,144,0.18),transparent_60%)]" />

                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold">Program snapshot</div>
                      <div className="text-xs text-[hsl(var(--cs-ink-2))]">Fast-read terms for developers</div>
                    </div>
                    <div className="rounded-full border border-black/10 bg-white/80 px-3 py-1 text-xs text-[hsl(var(--cs-ink-2))]">
                      Updated Aug 2025
                    </div>
                  </div>

                  <div className="mt-5 grid gap-3">
                    <StatPill icon={Landmark} label="Loan amount" value={PROGRAM.loanAmountRange} />
                    <StatPill icon={CheckCircle2} label="Loan-to-cost" value={PROGRAM.loanToCost} />
                    <StatPill icon={Clock} label="Closing timeline" value={PROGRAM.closingTimeline} />
                    <StatPill icon={FileClock} label="Draw options" value={PROGRAM.drawRequests.snap} />
                  </div>

                  <div className="mt-5 rounded-[1.05rem] border border-black/10 bg-white/80 p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <Building2 className="h-4 w-4 text-[hsl(var(--cs-green))]" />
                      Eligible property types
                    </div>
                    <ul className="mt-3 space-y-2 text-sm text-[hsl(var(--cs-ink-2))]">
                      {PROGRAM.propertyTypes.slice(0, 4).map((t) => (
                        <li key={t} className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-black/35 mt-0.5" />
                          <span>{t}</span>
                        </li>
                      ))}
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-black/35 mt-0.5" />
                        <span>{PROGRAM.propertyTypes[4]}</span>
                      </li>
                    </ul>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="text-xs text-[hsl(var(--cs-ink-2))]">
                      Minimum FICO: <span className="text-[hsl(var(--cs-ink))] font-semibold">{PROGRAM.ficoMin}</span>
                    </div>
                    <button
                      onClick={() => scrollTo(contactRef, "contact")}
                      className="inline-flex items-center gap-2 text-xs font-semibold text-[hsl(var(--cs-green))] hover:text-[hsl(var(--cs-green)/0.85)] transition-colors"
                    >
                      Request terms sheet
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-[1.05rem] border border-black/10 bg-white/75 backdrop-blur-xl p-4">
                  <div className="text-xs text-[hsl(var(--cs-ink-2))]">Land advance</div>
                  <div className="mt-1 text-sm font-semibold">Up to 75% (permitted)</div>
                  <div className="mt-1 text-xs text-[hsl(var(--cs-ink-2))]">Up to 60% (not permitted)</div>
                </div>
                <div className="rounded-[1.05rem] border border-black/10 bg-white/75 backdrop-blur-xl p-4">
                  <div className="text-xs text-[hsl(var(--cs-ink-2))]">Term length</div>
                  <div className="mt-1 text-sm font-semibold">Up to 12 months</div>
                  <div className="mt-1 text-xs text-[hsl(var(--cs-ink-2))]">Horizontal / Vertical</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Terms */}
      <section ref={termsRef} id="terms" className="relative border-t border-black/10">
        <div className="mx-auto max-w-6xl px-4 py-14 md:py-18">
          <SectionTitle
            eyebrow="Clarity upfront"
            title="Terms you can underwrite in minutes"
            desc="A quick overview of the program — designed to align expectations before you send a full package."
          />

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            <Card className="border-black/10 bg-white/75 text-[hsl(var(--cs-ink))] backdrop-blur-xl transition-colors hover:bg-white/7">
              <CardHeader>
                <CardTitle className="text-base">Pricing & leverage</CardTitle>
                <CardDescription className="text-[hsl(var(--cs-ink-2))]">
                  Competitive rates with high LTC for qualified sponsors.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="text-sm text-[hsl(var(--cs-ink-2))]">Rates starting at</div>
                  <div className="text-sm font-semibold">{PROGRAM.ratesStartingAt}</div>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div className="text-sm text-[hsl(var(--cs-ink-2))]">Loan-to-cost</div>
                  <div className="text-sm font-semibold">{PROGRAM.loanToCost}</div>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div className="text-sm text-[hsl(var(--cs-ink-2))]">Construction costs</div>
                  <div className="text-sm font-semibold">Up to 100% funded</div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-black/10 bg-white/75 text-[hsl(var(--cs-ink))] backdrop-blur-xl transition-colors hover:bg-white/7">
              <CardHeader>
                <CardTitle className="text-base">Borrower profile</CardTitle>
                <CardDescription className="text-[hsl(var(--cs-ink-2))]">
                  Built for repeat operators with verified execution.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="text-sm text-[hsl(var(--cs-ink-2))]">Minimum FICO</div>
                  <div className="text-sm font-semibold">{PROGRAM.ficoMin}</div>
                </div>
                <div className="text-sm text-[hsl(var(--cs-ink-2))] leading-relaxed">
                  <span className="font-semibold text-[hsl(var(--cs-ink))]">Experienced developers only</span>. Similar size completed projects on track record in the last 3 years.
                </div>
              </CardContent>
            </Card>

            <Card className="border-black/10 bg-white/75 text-[hsl(var(--cs-ink))] backdrop-blur-xl transition-colors hover:bg-white/7">
              <CardHeader>
                <CardTitle className="text-base">Speed to close</CardTitle>
                <CardDescription className="text-[hsl(var(--cs-ink-2))]">
                  Timelines optimized for active pipelines.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="text-sm text-[hsl(var(--cs-ink-2))]">Closing timeline</div>
                  <div className="text-sm font-semibold">{PROGRAM.closingTimeline}</div>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div className="text-sm text-[hsl(var(--cs-ink-2))]">Snap draws</div>
                  <div className="text-sm font-semibold">24 hours</div>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div className="text-sm text-[hsl(var(--cs-ink-2))]">Traditional inspection</div>
                  <div className="text-sm font-semibold">3–5 days</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Process */}
      <section ref={processRef} id="process" className="relative border-t border-black/10">
        <div className="mx-auto max-w-6xl px-4 py-14 md:py-18">
          <SectionTitle
            eyebrow="Designed for execution"
            title="Draws and closing that keep projects moving"
            desc="A simple, predictable workflow — so your budget stays intact and your schedule stays credible."
          />

          <div className="mt-10 grid gap-4 md:grid-cols-12">
            <div className="md:col-span-7">
              <div className="rounded-[1.25rem] border border-black/10 bg-white/75 backdrop-blur-xl p-6">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Clock className="h-4 w-4 text-[hsl(var(--cs-green))]" />
                  Closing timeline
                </div>
                <div className="mt-2 text-sm text-[hsl(var(--cs-ink-2))] leading-relaxed">
                  Typical close in <span className="text-[hsl(var(--cs-ink))] font-semibold">{PROGRAM.closingTimeline}</span> once the package is complete.
                  We focus on upfront clarity to reduce re-trades and last-minute document churn.
                </div>

                <Separator className="my-5 bg-white/10" />

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-[1.05rem] border border-black/10 bg-white/80 p-4">
                    <div className="text-sm font-semibold">Snap Draws</div>
                    <div className="mt-1 text-sm text-[hsl(var(--cs-ink-2))]">
                      Request funds, get them funded in ~24 hours.
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-xs text-[hsl(var(--cs-ink-2))]">
                      <FileClock className="h-4 w-4" />
                      Great for repeat scopes & predictable line items
                    </div>
                  </div>
                  <div className="rounded-[1.05rem] border border-black/10 bg-white/80 p-4">
                    <div className="text-sm font-semibold">Traditional Inspections</div>
                    <div className="mt-1 text-sm text-[hsl(var(--cs-ink-2))]">
                      Field inspection + funding in ~3–5 days.
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-xs text-[hsl(var(--cs-ink-2))]">
                      <CheckCircle2 className="h-4 w-4" />
                      Best for complex scopes & first-time budgets
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="rounded-[1.25rem] border border-black/10 bg-[linear-gradient(180deg,rgba(232,196,144,0.12),rgba(255,255,255,0.05))] backdrop-blur-xl p-6">
                <div className="flex items-center gap-2 text-sm font-semibold text-[hsl(var(--cs-ink))]">
                  <Landmark className="h-4 w-4 text-white bg-[hsl(var(--cs-navy))] bg-[linear-gradient(180deg,hsl(var(--cs-navy)),hsl(var(--cs-navy)/0.92))] hover:shadow-[0_12px_28px_rgba(15,102,84,0.14)] transition-colors rounded-md p-0.5" />
                  Land advance guidance
                </div>
                <p className="mt-2 text-sm text-[hsl(var(--cs-ink-2))] leading-relaxed">
                  Increase leverage on land when entitlement risk is reduced.
                </p>
                <ul className="mt-4 space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[hsl(var(--cs-ink-2))] mt-0.5" />
                    <span className="text-[hsl(var(--cs-ink-2))]">{PROGRAM.landAdvance.permitted}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[hsl(var(--cs-ink-2))] mt-0.5" />
                    <span className="text-[hsl(var(--cs-ink-2))]">{PROGRAM.landAdvance.notPermitted}</span>
                  </li>
                </ul>

                <div className="mt-5 rounded-[1.05rem] border border-black/10 bg-white/80 p-4">
                  <div className="text-xs text-[hsl(var(--cs-ink-2))]">Reminder</div>
                  <div className="mt-1 text-sm text-[hsl(var(--cs-ink-2))]">
                    We focus on sponsors who have executed similar projects recently.
                  </div>
                </div>

                <Button
                  className="mt-5 w-full bg-[hsl(var(--cs-navy))] bg-[linear-gradient(180deg,hsl(var(--cs-navy)),hsl(var(--cs-navy)/0.92))] hover:shadow-[0_12px_28px_rgba(15,102,84,0.14)] transition-colors text-white hover:bg-[hsl(var(--cs-navy)/0.94)]"
                  onClick={() => scrollTo(contactRef, "contact")}
                >
                  Talk to a lender
                  <Phone className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section ref={faqRef} id="faq" className="relative border-t border-black/10">
        <div className="mx-auto max-w-6xl px-4 py-14 md:py-18">
          <SectionTitle
            eyebrow="Answers before you send a package"
            title="FAQ"
            desc="If you don’t see your question here, request terms and we’ll respond with specifics for your project." 
          />

          <div className="mt-10 grid gap-6 md:grid-cols-12">
            <div className="md:col-span-7">
              <div className="rounded-[1.25rem] border border-black/10 bg-white/75 backdrop-blur-xl p-6">
                <Accordion type="single" collapsible className="w-full">
                  {FAQS.map((f, idx) => (
                    <AccordionItem key={f.q} value={`item-${idx}`} className="border-black/10">
                      <AccordionTrigger className="text-[hsl(var(--cs-ink))] hover:no-underline">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-[hsl(var(--cs-ink-2))] leading-relaxed">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="rounded-[1.25rem] border border-black/10 bg-white/75 backdrop-blur-xl p-6">
                <div className="text-sm font-semibold">What to send for a fast quote</div>
                <p className="mt-2 text-sm text-[hsl(var(--cs-ink-2))]">
                  Keep it simple. If you have these, we can move quickly:
                </p>
                <ul className="mt-4 space-y-3 text-sm text-[hsl(var(--cs-ink-2))]">
                  {[
                    "Project address + property type",
                    "Total cost + sources/uses",
                    "Budget + draw preference",
                    "Sponsor track record (last 3 years)",
                    "Entity info + contact details",
                  ].map((x) => (
                    <li key={x} className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-[hsl(var(--cs-green))] mt-0.5" />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>

                <Separator className="my-5 bg-white/10" />

                <div className="text-xs text-[hsl(var(--cs-ink-2))]">Persistence</div>
                <div className="mt-1 text-sm text-[hsl(var(--cs-ink-2))]">
                  Requests are saved securely in our database and available to your team.
                </div>
                <div className="mt-2 text-xs text-[hsl(var(--cs-ink-2))]">
                  Admin action: clearing will remove all saved requests.
                </div>
                <div className="mt-4 flex items-center justify-between gap-3">
                  <div className="text-xs text-[hsl(var(--cs-ink-2))]">
                    Saved requests: <span className="text-[hsl(var(--cs-ink))] font-semibold">{recentCount}</span>
                  </div>
                  <Button
                    variant="secondary"
                    className="border-black/10 bg-white/75 text-[hsl(var(--cs-ink))] hover:bg-white/10"
                    onClick={clearDemo}
                  >
                    Clear requests
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section ref={contactRef} id="contact" className="relative border-t border-black/10">
        <div className="mx-auto max-w-6xl px-4 py-14 md:py-18">
          <div className="grid gap-10 md:grid-cols-12 md:items-start">
            <div className="md:col-span-5">
              <SectionTitle
                eyebrow="Request terms"
                title="Tell us about your project"
                desc="We’ll respond with next steps and the specific package needed to price your deal."
              />

              <div className="mt-7 rounded-[1.25rem] border border-black/10 bg-[linear-gradient(180deg,rgba(232,196,144,0.16),rgba(255,255,255,0.04))] backdrop-blur-xl p-6">
                <div className="flex items-start gap-3">
                  <div className="rounded-[1.05rem] bg-black/25 border border-black/10 p-2">
                    <ShieldCheck className="h-5 w-5 text-[hsl(var(--cs-green))]" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Built for experienced sponsors</div>
                    <div className="mt-1 text-sm text-[hsl(var(--cs-ink-2))] leading-relaxed">
                      If you’ve executed similar projects in the past 3 years, this program is designed to move quickly.
                    </div>
                  </div>
                </div>

                <Separator className="my-5 bg-white/10" />

                <div className="space-y-3 text-sm text-[hsl(var(--cs-ink-2))]">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[hsl(var(--cs-ink-2))]">Loan amount</span>
                    <span className="font-semibold">{PROGRAM.loanAmountRange}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[hsl(var(--cs-ink-2))]">Terms</span>
                    <span className="font-semibold">Up to 12 months</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[hsl(var(--cs-ink-2))]">Draw speed</span>
                    <span className="font-semibold">24h / 3–5d</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-7">
              <div className="rounded-[1.25rem] border border-black/10 bg-white/75 backdrop-blur-xl p-6 md:p-8">
                <form onSubmit={onSubmit} className="space-y-5">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-sm text-[hsl(var(--cs-ink-2))]">Full name</label>
                      <Input
                        value={values.name}
                        onChange={(e) => setValues((s) => ({ ...s, name: e.target.value }))}
                        onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                        placeholder="Jane Developer"
                        className={cn(
                          "mt-2 border-black/10 bg-white/80 text-[hsl(var(--cs-ink))] placeholder:text-black/35 focus-visible:ring-[hsl(var(--cs-navy))]",
                          hasError("name") && "border-[rgba(255,90,90,0.6)]"
                        )}
                      />
                      {hasError("name") ? (
                        <div className="mt-1 text-xs text-[rgba(255,120,120,0.9)]">{errors.name}</div>
                      ) : null}
                    </div>
                    <div>
                      <label className="text-sm text-[hsl(var(--cs-ink-2))]">Company</label>
                      <Input
                        value={values.company}
                        onChange={(e) => setValues((s) => ({ ...s, company: e.target.value }))}
                        onBlur={() => setTouched((t) => ({ ...t, company: true }))}
                        placeholder="Your development company"
                        className={cn(
                          "mt-2 border-black/10 bg-white/80 text-[hsl(var(--cs-ink))] placeholder:text-black/35 focus-visible:ring-[hsl(var(--cs-navy))]",
                          hasError("company") && "border-[rgba(255,90,90,0.6)]"
                        )}
                      />
                      {hasError("company") ? (
                        <div className="mt-1 text-xs text-[rgba(255,120,120,0.9)]">{errors.company}</div>
                      ) : null}
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-sm text-[hsl(var(--cs-ink-2))]">Email</label>
                      <Input
                        value={values.email}
                        onChange={(e) => setValues((s) => ({ ...s, email: e.target.value }))}
                        onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                        placeholder="you@company.com"
                        className={cn(
                          "mt-2 border-black/10 bg-white/80 text-[hsl(var(--cs-ink))] placeholder:text-black/35 focus-visible:ring-[hsl(var(--cs-navy))]",
                          hasError("email") && "border-[rgba(255,90,90,0.6)]"
                        )}
                      />
                      {hasError("email") ? (
                        <div className="mt-1 text-xs text-[rgba(255,120,120,0.9)]">{errors.email}</div>
                      ) : null}
                    </div>
                    <div>
                      <label className="text-sm text-[hsl(var(--cs-ink-2))]">Phone</label>
                      <Input
                        value={values.phone}
                        onChange={(e) => setValues((s) => ({ ...s, phone: e.target.value }))}
                        onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                        placeholder="(555) 123-4567"
                        className={cn(
                          "mt-2 border-black/10 bg-white/80 text-[hsl(var(--cs-ink))] placeholder:text-black/35 focus-visible:ring-[hsl(var(--cs-navy))]",
                          hasError("phone") && "border-[rgba(255,90,90,0.6)]"
                        )}
                      />
                      {hasError("phone") ? (
                        <div className="mt-1 text-xs text-[rgba(255,120,120,0.9)]">{errors.phone}</div>
                      ) : null}
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <label className="text-sm text-[hsl(var(--cs-ink-2))]">Property type</label>
                      <Select
                        value={values.propertyType}
                        onValueChange={(v) => {
                          setValues((s) => ({ ...s, propertyType: v }));
                          setTouched((t) => ({ ...t, propertyType: true }));
                        }}
                      >
                        <SelectTrigger
                          className={cn(
                            "mt-2 border-black/10 bg-white/80 text-[hsl(var(--cs-ink))] focus:ring-1 focus:ring-[hsl(var(--cs-navy))]",
                            hasError("propertyType") && "border-[rgba(255,90,90,0.6)]"
                          )}
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="border-black/10 bg-[hsl(var(--cs-bg-2))] text-[hsl(var(--cs-ink))]">
                          {PROPERTY_TYPE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {hasError("propertyType") ? (
                        <div className="mt-1 text-xs text-[rgba(255,120,120,0.9)]">{errors.propertyType}</div>
                      ) : null}
                    </div>

                    <div>
                      <label className="text-sm text-[hsl(var(--cs-ink-2))]">Loan amount</label>
                      <Select
                        value={values.loanAmount}
                        onValueChange={(v) => {
                          setValues((s) => ({ ...s, loanAmount: v }));
                          setTouched((t) => ({ ...t, loanAmount: true }));
                        }}
                      >
                        <SelectTrigger
                          className={cn(
                            "mt-2 border-black/10 bg-white/80 text-[hsl(var(--cs-ink))] focus:ring-1 focus:ring-[hsl(var(--cs-navy))]",
                            hasError("loanAmount") && "border-[rgba(255,90,90,0.6)]"
                          )}
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="border-black/10 bg-[hsl(var(--cs-bg-2))] text-[hsl(var(--cs-ink))]">
                          {LOAN_AMOUNT_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {hasError("loanAmount") ? (
                        <div className="mt-1 text-xs text-[rgba(255,120,120,0.9)]">{errors.loanAmount}</div>
                      ) : null}
                    </div>

                    <div>
                      <label className="text-sm text-[hsl(var(--cs-ink-2))]">Timeline</label>
                      <Select
                        value={values.timeline}
                        onValueChange={(v) => {
                          setValues((s) => ({ ...s, timeline: v }));
                          setTouched((t) => ({ ...t, timeline: true }));
                        }}
                      >
                        <SelectTrigger
                          className={cn(
                            "mt-2 border-black/10 bg-white/80 text-[hsl(var(--cs-ink))] focus:ring-1 focus:ring-[hsl(var(--cs-navy))]",
                            hasError("timeline") && "border-[rgba(255,90,90,0.6)]"
                          )}
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="border-black/10 bg-[hsl(var(--cs-bg-2))] text-[hsl(var(--cs-ink))]">
                          {TIMELINE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {hasError("timeline") ? (
                        <div className="mt-1 text-xs text-[rgba(255,120,120,0.9)]">{errors.timeline}</div>
                      ) : null}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-[hsl(var(--cs-ink-2))]">Project details</label>
                    <Textarea
                      value={values.message}
                      onChange={(e) => setValues((s) => ({ ...s, message: e.target.value }))}
                      onBlur={() => setTouched((t) => ({ ...t, message: true }))}
                      placeholder="Location, number of units/lots, current stage (entitled/permitted), and any time-sensitive constraints..."
                      className={cn(
                        "mt-2 min-h-[120px] border-black/10 bg-white/80 text-[hsl(var(--cs-ink))] placeholder:text-black/35 focus-visible:ring-[hsl(var(--cs-navy))]",
                        hasError("message") && "border-[rgba(255,90,90,0.6)]"
                      )}
                    />
                    {hasError("message") ? (
                      <div className="mt-1 text-xs text-[rgba(255,120,120,0.9)]">{errors.message}</div>
                    ) : null}
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
                    <div className="text-xs text-[hsl(var(--cs-ink-2))]">
                      By submitting, you agree to be contacted about your request.
                    </div>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className={cn(
                        "bg-[hsl(var(--cs-navy))] bg-[linear-gradient(180deg,hsl(var(--cs-navy)),hsl(var(--cs-navy)/0.92))] hover:shadow-[0_12px_28px_rgba(15,102,84,0.14)] transition-colors text-white hover:bg-[hsl(var(--cs-navy)/0.94)] min-w-[190px]",
                        submitting && "opacity-90"
                      )}
                    >
                      {submitting ? "Submitting…" : "Submit request"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>

                  <div className="pt-2 text-xs text-[hsl(var(--cs-ink-2))]">
                    Disclaimer: Rates and terms subject to underwriting; this is not a commitment to lend.
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-black/10 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto max-w-6xl px-4 py-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="CrowdStructure" className="h-8 w-auto rounded-md bg-white p-1" />
              <div>
                <div className="text-sm font-semibold">CrowdStructure</div>
                <div className="text-xs text-[hsl(var(--cs-ink-2))]">New construction lending</div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <NavLink href="#terms">Terms</NavLink>
              <NavLink href="#process">Draws</NavLink>
              <NavLink href="#faq">FAQ</NavLink>
              <a
                href="https://www.crowdstructure.com"
                target="_blank"
                rel="noreferrer"
                className="text-sm font-medium text-[hsl(var(--cs-ink-2))] hover:text-[hsl(var(--cs-ink))] transition-colors"
              >
                Website
              </a>
            </div>
          </div>

          <Separator className="my-6 bg-white/10" />
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs text-[hsl(var(--cs-ink-2))]">
            <div>
              © {new Date().getFullYear()} CrowdStructure. All rights reserved.
            </div>
            <div className="max-w-2xl">
              Rates and terms subject to change. This page is for informational purposes only and does not constitute a commitment to lend.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
